import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  increment, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp,
  addDoc 
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Analytics Service - Handles all operations related to analytics data
 */
const analyticsService = {
  /**
   * Track campaign view
   * @param {string} campaignId - Campaign ID
   * @param {string} userId - User ID (optional)
   * @returns {Promise} - Promise that resolves when view is tracked
   */
  trackCampaignView: async (campaignId, userId = null) => {
    try {
      // Update campaign view count
      const campaignRef = doc(db, 'campaigns', campaignId);
      await updateDoc(campaignRef, {
        'analytics.views': increment(1),
        updatedAt: serverTimestamp()
      });
      
      // Add to analytics collection for time-series data
      const analyticsRef = collection(db, 'analytics');
      const eventData = {
        type: 'campaign_view',
        campaignId,
        timestamp: serverTimestamp()
      };
      
      if (userId) {
        eventData.userId = userId;
      }
      
      await addDoc(analyticsRef, eventData);
    } catch (error) {
      console.error('Error tracking campaign view:', error);
      throw error;
    }
  },

  /**
   * Track user event
   * @param {string} userId - User ID
   * @param {string} eventType - Event type
   * @param {object} eventData - Event data
   * @returns {Promise} - Promise that resolves when event is tracked
   */
  trackUserEvent: async (userId, eventType, eventData = {}) => {
    try {
      const analyticsRef = collection(db, 'analytics');
      
      const data = {
        type: eventType,
        userId,
        timestamp: serverTimestamp(),
        ...eventData
      };
      
      await addDoc(analyticsRef, data);
    } catch (error) {
      console.error('Error tracking user event:', error);
      throw error;
    }
  },

  /**
   * Track content engagement
   * @param {string} submissionId - Content submission ID
   * @param {string} engagementType - Engagement type ('view', 'like', 'comment', 'share', 'click')
   * @param {object} engagementData - Engagement data
   * @returns {Promise} - Promise that resolves when engagement is tracked
   */
  trackContentEngagement: async (submissionId, engagementType, engagementData = {}) => {
    try {
      // Get submission
      const submissionRef = doc(db, 'contentSubmissions', submissionId);
      const submissionDoc = await getDoc(submissionRef);
      
      if (!submissionDoc.exists()) {
        throw new Error(`Submission with ID ${submissionId} not found`);
      }
      
      const submission = submissionDoc.data();
      
      // Update submission analytics
      const analyticsField = `analytics.${engagementType}s`; // e.g., analytics.likes
      await updateDoc(submissionRef, {
        [analyticsField]: increment(1),
        statusUpdatedAt: serverTimestamp()
      });
      
      // Add to analytics collection for time-series data
      const analyticsRef = collection(db, 'analytics');
      const eventData = {
        type: 'content_engagement',
        submissionId,
        campaignId: submission.campaignId,
        influencerId: submission.influencerId,
        engagementType,
        timestamp: serverTimestamp(),
        ...engagementData
      };
      
      await addDoc(analyticsRef, eventData);
      
      // If engagement type is view, update participant reach
      if (engagementType === 'view') {
        const participantRef = doc(db, 'campaignParticipants', submission.participantId);
        await updateDoc(participantRef, {
          'analytics.reach': increment(1),
          statusUpdatedAt: serverTimestamp()
        });
        
        // Update campaign total reach
        const campaignRef = doc(db, 'campaigns', submission.campaignId);
        await updateDoc(campaignRef, {
          'analytics.totalReach': increment(1),
          updatedAt: serverTimestamp()
        });
      }
      
      // If engagement type is like, comment, or share, update participant engagement
      if (['like', 'comment', 'share'].includes(engagementType)) {
        const participantRef = doc(db, 'campaignParticipants', submission.participantId);
        await updateDoc(participantRef, {
          'analytics.engagement': increment(1),
          statusUpdatedAt: serverTimestamp()
        });
        
        // Update campaign total engagement
        const campaignRef = doc(db, 'campaigns', submission.campaignId);
        await updateDoc(campaignRef, {
          'analytics.totalEngagement': increment(1),
          updatedAt: serverTimestamp()
        });
      }
      
      // If engagement type is click, update participant clicks
      if (engagementType === 'click') {
        const participantRef = doc(db, 'campaignParticipants', submission.participantId);
        await updateDoc(participantRef, {
          'analytics.clicks': increment(1),
          statusUpdatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error tracking content engagement:', error);
      throw error;
    }
  },

  /**
   * Get campaign analytics
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<object>} - Campaign analytics data
   */
  getCampaignAnalytics: async (campaignId) => {
    try {
      const campaignRef = doc(db, 'campaigns', campaignId);
      const campaignDoc = await getDoc(campaignRef);
      
      if (!campaignDoc.exists()) {
        throw new Error(`Campaign with ID ${campaignId} not found`);
      }
      
      return campaignDoc.data().analytics || {
        views: 0,
        applications: 0,
        approvedApplications: 0,
        submittedContent: 0,
        approvedContent: 0,
        totalReach: 0,
        totalEngagement: 0
      };
    } catch (error) {
      console.error('Error getting campaign analytics:', error);
      throw error;
    }
  },

  /**
   * Get brand analytics
   * @param {string} brandId - Brand user ID
   * @returns {Promise<object>} - Brand analytics data
   */
  getBrandAnalytics: async (brandId) => {
    try {
      // Get all campaigns by this brand
      const campaignsRef = collection(db, 'campaigns');
      const q = query(campaignsRef, where('brandId', '==', brandId));
      const querySnapshot = await getDocs(q);
      
      // Initialize analytics object
      const analytics = {
        totalCampaigns: querySnapshot.size,
        activeCampaigns: 0,
        completedCampaigns: 0,
        draftCampaigns: 0,
        totalApplications: 0,
        totalApprovedApplications: 0,
        totalSubmittedContent: 0,
        totalApprovedContent: 0,
        totalReach: 0,
        totalEngagement: 0,
        campaignPerformance: []
      };
      
      // Aggregate data from all campaigns
      querySnapshot.forEach((doc) => {
        const campaign = doc.data();
        const campaignAnalytics = campaign.analytics || {};
        
        // Count campaigns by status
        if (campaign.status === 'active') {
          analytics.activeCampaigns++;
        } else if (campaign.status === 'completed') {
          analytics.completedCampaigns++;
        } else if (campaign.status === 'draft') {
          analytics.draftCampaigns++;
        }
        
        // Aggregate analytics
        analytics.totalApplications += campaignAnalytics.applications || 0;
        analytics.totalApprovedApplications += campaignAnalytics.approvedApplications || 0;
        analytics.totalSubmittedContent += campaignAnalytics.submittedContent || 0;
        analytics.totalApprovedContent += campaignAnalytics.approvedContent || 0;
        analytics.totalReach += campaignAnalytics.totalReach || 0;
        analytics.totalEngagement += campaignAnalytics.totalEngagement || 0;
        
        // Add campaign performance data
        analytics.campaignPerformance.push({
          id: doc.id,
          title: campaign.title,
          status: campaign.status,
          views: campaignAnalytics.views || 0,
          applications: campaignAnalytics.applications || 0,
          approvedApplications: campaignAnalytics.approvedApplications || 0,
          submittedContent: campaignAnalytics.submittedContent || 0,
          approvedContent: campaignAnalytics.approvedContent || 0,
          totalReach: campaignAnalytics.totalReach || 0,
          totalEngagement: campaignAnalytics.totalEngagement || 0
        });
      });
      
      // Sort campaign performance by reach (descending)
      analytics.campaignPerformance.sort((a, b) => b.totalReach - a.totalReach);
      
      return analytics;
    } catch (error) {
      console.error('Error getting brand analytics:', error);
      throw error;
    }
  },

  /**
   * Get influencer analytics
   * @param {string} influencerId - Influencer user ID
   * @returns {Promise<object>} - Influencer analytics data
   */
  getInfluencerAnalytics: async (influencerId) => {
    try {
      // Get all campaign participants for this influencer
      const participantsRef = collection(db, 'campaignParticipants');
      const q = query(participantsRef, where('influencerId', '==', influencerId));
      const querySnapshot = await getDocs(q);
      
      // Initialize analytics object
      const analytics = {
        totalCampaigns: 0,
        approvedCampaigns: 0,
        pendingCampaigns: 0,
        rejectedCampaigns: 0,
        totalSubmissions: 0,
        approvedSubmissions: 0,
        totalReach: 0,
        totalEngagement: 0,
        totalClicks: 0,
        campaignPerformance: []
      };
      
      // Aggregate data from all participations
      querySnapshot.forEach((doc) => {
        const participant = doc.data();
        const participantAnalytics = participant.analytics || {};
        
        analytics.totalCampaigns++;
        
        // Count campaigns by status
        if (participant.status === 'approved') {
          analytics.approvedCampaigns++;
        } else if (participant.status === 'pending') {
          analytics.pendingCampaigns++;
        } else if (participant.status === 'rejected') {
          analytics.rejectedCampaigns++;
        }
        
        // Count submissions
        if (participant.submissionStatus === 'submitted' || 
            participant.submissionStatus === 'approved' || 
            participant.submissionStatus === 'revision_requested') {
          analytics.totalSubmissions++;
          
          if (participant.submissionStatus === 'approved') {
            analytics.approvedSubmissions++;
          }
        }
        
        // Aggregate analytics
        analytics.totalReach += participantAnalytics.reach || 0;
        analytics.totalEngagement += participantAnalytics.engagement || 0;
        analytics.totalClicks += participantAnalytics.clicks || 0;
        
        // Add campaign performance data
        analytics.campaignPerformance.push({
          campaignId: participant.campaignId,
          participantId: doc.id,
          status: participant.status,
          submissionStatus: participant.submissionStatus,
          reach: participantAnalytics.reach || 0,
          engagement: participantAnalytics.engagement || 0,
          clicks: participantAnalytics.clicks || 0
        });
      });
      
      // Sort campaign performance by reach (descending)
      analytics.campaignPerformance.sort((a, b) => b.reach - a.reach);
      
      // Calculate engagement rate
      if (analytics.totalReach > 0) {
        analytics.engagementRate = (analytics.totalEngagement / analytics.totalReach) * 100;
      } else {
        analytics.engagementRate = 0;
      }
      
      return analytics;
    } catch (error) {
      console.error('Error getting influencer analytics:', error);
      throw error;
    }
  },

  /**
   * Get platform analytics
   * @returns {Promise<object>} - Platform-wide analytics data
   */
  getPlatformAnalytics: async () => {
    try {
      // Get counts from collections
      const [
        usersSnapshot,
        brandsSnapshot,
        influencersSnapshot,
        campaignsSnapshot,
        activeSnapshot,
        completedSnapshot,
        submissionsSnapshot,
        approvedSubmissionsSnapshot
      ] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(query(collection(db, 'users'), where('userType', '==', 'brand'))),
        getDocs(query(collection(db, 'users'), where('userType', '==', 'influencer'))),
        getDocs(collection(db, 'campaigns')),
        getDocs(query(collection(db, 'campaigns'), where('status', '==', 'active'))),
        getDocs(query(collection(db, 'campaigns'), where('status', '==', 'completed'))),
        getDocs(collection(db, 'contentSubmissions')),
        getDocs(query(collection(db, 'contentSubmissions'), where('status', '==', 'approved')))
      ]);
      
      // Aggregate total reach and engagement
      let totalReach = 0;
      let totalEngagement = 0;
      
      campaignsSnapshot.forEach(doc => {
        const campaign = doc.data();
        const analytics = campaign.analytics || {};
        totalReach += analytics.totalReach || 0;
        totalEngagement += analytics.totalEngagement || 0;
      });
      
      // Create analytics object
      const analytics = {
        totalUsers: usersSnapshot.size,
        totalBrands: brandsSnapshot.size,
        totalInfluencers: influencersSnapshot.size,
        totalCampaigns: campaignsSnapshot.size,
        activeCampaigns: activeSnapshot.size,
        completedCampaigns: completedSnapshot.size,
        totalSubmissions: submissionsSnapshot.size,
        approvedSubmissions: approvedSubmissionsSnapshot.size,
        totalReach,
        totalEngagement,
        engagementRate: totalReach > 0 ? (totalEngagement / totalReach) * 100 : 0
      };
      
      return analytics;
    } catch (error) {
      console.error('Error getting platform analytics:', error);
      throw error;
    }
  },

  /**
   * Get analytics events by type
   * @param {string} eventType - Event type
   * @param {object} options - Query options (timeframe, limit, etc.)
   * @returns {Promise<Array>} - Array of analytics events
   */
  getAnalyticsEventsByType: async (eventType, options = {}) => {
    try {
      const analyticsRef = collection(db, 'analytics');
      
      // Build query
      let q = query(analyticsRef, where('type', '==', eventType));
      
      // Add timeframe filter if provided
      if (options.startTime) {
        q = query(q, where('timestamp', '>=', options.startTime));
      }
      
      if (options.endTime) {
        q = query(q, where('timestamp', '<=', options.endTime));
      }
      
      // Add ordering
      q = query(q, orderBy('timestamp', 'desc'));
      
      // Add limit if provided
      if (options.limit) {
        q = query(q, limit(options.limit));
      }
      
      const querySnapshot = await getDocs(q);
      
      const events = [];
      querySnapshot.forEach((doc) => {
        events.push({ id: doc.id, ...doc.data() });
      });
      
      return events;
    } catch (error) {
      console.error('Error getting analytics events by type:', error);
      throw error;
    }
  }
};

export default analyticsService;
